/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
import 'lazysizes';
import { gsap } from 'gsap';
import { debounce } from 'lodash';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import Rellax from 'rellax';
import Vue from 'vue';
import Router from './router';
import { ScrollTrigger } from './gsap-bonus/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

require('./bootstrap');

require('./components/book-demo-component');
require('./components/call-back-component');
require('./components/cookiebot-component');
require('./components/dropdown-component');
require('./dsg/components/dynamic-background-component');
require('./components/file-upload-component');
require('./components/gallery-component');
require('./components/general-form-component');
require('./components/job-application-component');
require('./components/load-filter-component');
require('./components/logo-ticker-component');
require('./components/pop-out-filter-component');
require('./components/quote-slider-component');
require('./components/simple-video-component');
require('./components/slider-component');
require('./components/story-slider-component');
require('./components/tabs-component');
require('./components/text-ticker-component');
require('./components/video-component');
require('./components/language-component');
require('./components/assessment-component');
require('./components/faq-component');
require('./components/whitepaper-component');
require('./components/home-slider-component');

const App = new Vue({
    el: '#app',
    delimiters: ['${', '}'],

    data() {
        return {
            router: {
                currentSection: null,
                instance: null,
                prefetch: true,
            },
            // router: null,
            isMobile: false,
            // isTablet: false,
            // scrollbar: null,
            showNavigation: false,
            toggleMenuItemsTl: null,
            // darkHeader: '1',
            // showFooter: '1'
        };
    },

    watch: {
        isMobile(value) {
            EventBus.$emit('is-mobile-changed');
        },
        isTablet(value) {
            EventBus.$emit('is-tablet-changed');
        },
        showNavigation(visibile) {
            EventBus.$emit('navigation-state-changed', visibile);

            if (this.showNavigation) {
                this.openNavigation();

                return;
            }

            this.closeNavigation();
        },
    },

    mounted() {
        this.initRevealTriggers();
        this.initScrollButton();
        // this.initScrollLanguage();
        this.initCareerButton();

        this.isMobile = window.innerWidth < 768;

        window.addEventListener('resize', this.onWindowResize);

        EventBus.$on('window-resize', () => {
            this.isMobile = window.innerWidth < 768;
            this.closeAllMenuToggles();
        });
    },

    methods: {
        openNavigation() {
            disableBodyScroll(document.querySelector('#navigation__container'), { reserveScrollBarGap: true });

            gsap.to('.navigation__menu-bar', { duration: 1.1, scaleX: 1 });
            gsap.to('.navigation__bg', { delay: 0.2, duration: 1.1, scaleX: '1' });
            gsap.to('.header-logo', { autoAlpha: 0 });
            gsap.to('.top-navigation', { autoAlpha: 0 });

            gsap.to(['.navigation__list'], {
                delay: 0.8,
                duration: 1,
                autoAlpha: 1,
                x: 0,
                ease: 'power3.out',
                // stagger: 0.1,
            });
            gsap.to('.navigation__contact-container', { delay: 0.8, duration: 1, autoAlpha: 1 });
        },

        closeNavigation() {
            enableBodyScroll(document.querySelector('#navigation__container'));

            gsap.to('.navigation__contact-container', { autoAlpha: 0 });
            gsap.to('.navigation__menu-bar', { delay: 0.2, duration: 1.1, scaleX: 0 });
            gsap.to('.navigation__bg', { delay: 0.1, duration: 1.1, scaleX: 0 });
            gsap.to('.header-logo', { delay: 0.4, autoAlpha: 1 });
            gsap.to('.top-navigation', { delay: 0.4, autoAlpha: 1 });

            gsap.to(['.navigation__list'], {
                duration: 1,
                autoAlpha: 0,
                x: '200px',
                ease: 'power3.out',
                // stagger: 0.1,
            });
        },

        initRevealTriggers() {
            const elements = [
                ...document.querySelectorAll('[data-reveal]'),
                ...document.querySelectorAll('[data-fade-reveal]'),
                ...document.querySelectorAll('[data-image-reveal]'),
            ];

            const countEl = [
                ...document.querySelectorAll('[data-count-reveal]'),
            ];

            if (countEl) {
                this.initCountRevealTriggers(countEl);
            }

            elements.forEach((element) => {
                const { revealDelay, revealManually, revealPosition } = element.dataset;
                if (revealManually === '') {
                    return;
                }

                const startPercentage = revealPosition || '90%';

                ScrollTrigger.create({
                    trigger: element,
                    start: `top ${startPercentage}`,
                    onEnter: () => {
                        if (!revealDelay) {
                            element.classList.add('--revealed');
                            return;
                        }

                        gsap.delayedCall(revealDelay, () => {
                            element.classList.add('--revealed');
                        });
                    },
                });
            });
        },

        initScrollButton() {
            ScrollTrigger.create({
                trigger: '#app',
                start: 'center 50%',
                toggleClass: { targets: '.to-top-button', className: 'active' },
            });
        },

        // initScrollLanguage() {
        //     ScrollTrigger.create({
        //         trigger: '#app',
        //         start: 'top 100%',
        //         toggleClass: { targets: '.language__selector', className: 'hidden' },
        //     });
        // },

        initCareerButton() {
            const element = document.getElementById('career__apply-cta');
            if (typeof (element) !== 'undefined' && element != null) {
                ScrollTrigger.create({
                    trigger: '#apply-for-job',
                    start: 'top 90%',
                    onEnter: () => document.getElementById('career__apply-cta').classList.add('hidden'),
                    onLeaveBack: () => document.getElementById('career__apply-cta').classList.remove('hidden'),
                });
            }
        },

        initCountRevealTriggers(el) {
            el.forEach((element) => {
                const { revealDelay, revealManually, revealPosition } = element.dataset;
                if (revealManually === '') {
                    return;
                }
                const startPercentage = revealPosition || '90%';

                ScrollTrigger.create({
                    trigger: element,
                    start: `top ${startPercentage}`,
                    onEnter: () => {
                        const { hasRun } = element.dataset;

                        if (hasRun) {
                            return;
                        }

                        element.setAttribute('data-has-run', true);

                        if (element.innerText.indexOf('.') > -1) {
                            const fixedTo = element.dataset.number.split('.').pop().length;
                            // console.log(fixedTo);
                            const count = element;
                            const zero = { val: 0 };

                            gsap.to(zero, {
                                val: count.dataset.number,
                                duration: 4,
                                ease: 'power1.easeOut',
                                modifiers: {
                                    val: (val) => val.toFixed(fixedTo),
                                },
                                onUpdate() {
                                    count.innerText = zero.val;
                                },
                            });
                            return;
                        }

                        gsap.from(element, {
                            textContent: 0,
                            duration: 4,
                            ease: 'power1.easeOut',
                            snap: { textContent: 1 },
                        });
                    },
                });
            });
        },

        toggleMenuItems(e, id) {
            const list = document.querySelector(`[data-id="${id}"]`);
            const arrow = e.target.querySelector('svg');
            const openLists = document.querySelectorAll('[data-is-open]');

            const { isOpen } = list.dataset;

            if (this.toggleMenuItemsTl) {
                this.toggleMenuItemsTl.kill();

                if (isOpen) {
                    list.removeAttribute('data-is-open');
                }

                if (!isOpen) {
                    list.setAttribute('data-is-open', true);
                }
            }

            this.toggleMenuItemsTl = gsap.timeline();

            // if opening a new menu, close all other open menus
            if (openLists.length > 0) {
                openLists.forEach((openList) => {
                    if (openList !== list) { // Check to ensure we don't close the current list being opened
                        this.toggleMenuItemsTl.to(openList, {
                            duration: 0.5,
                            height: 0,
                            ease: 'power4.in',
                            onComplete: () => {
                                openList.removeAttribute('data-is-open');
                            },
                        });

                        const parentArrow = openList.parentNode.querySelector('svg');
                        if (parentArrow) {
                            this.toggleMenuItemsTl.to(parentArrow, {
                                duration: 0.25,
                                rotate: 0,
                                ease: 'power4.in',
                            }, '<');
                        }
                    }
                });
            }

            if (isOpen) {
                this.toggleMenuItemsTl
                    .to(list, {
                        duration: 1,
                        height: 0,
                        ease: 'power4.out',
                        onComplete: () => {
                            list.removeAttribute('data-is-open');
                        },
                    }, '<')
                    .to(arrow, {
                        duration: 0.5,
                        rotate: 0,
                        ease: 'power4.out',
                    }, '<');

                return;
            }

            this.toggleMenuItemsTl
                .to(list, {
                    duration: 1,
                    height: 'auto',
                    ease: 'power4.out',
                    onComplete: () => {
                        list.setAttribute('data-is-open', true);
                    },
                })
                .to(arrow, {
                    duration: 0.5,
                    rotate: 180,
                    ease: 'power4.out',
                }, '<');
        },

        closeAllMenuToggles() {
            const hasOpenMenus = document.querySelectorAll('[data-is-open]');

            if (hasOpenMenus.length === 0) {
                return;
            }

            for (let i = 0; i < hasOpenMenus.length; i++) {
                const list = hasOpenMenus[i];
                const parent = hasOpenMenus[i].parentNode;
                const arrow = parent.querySelector('svg');

                this.toggleMenuItemsTl
                    .to(list, {
                        duration: 1,
                        height: 0,
                        ease: 'power4.out',
                        onComplete: () => {
                            list.removeAttribute('data-is-open');
                        },
                    })
                    .to(arrow, {
                        duration: 0.5,
                        rotate: 0,
                        ease: 'power4.out',
                    }, '<');
            }
        },

        /**
         * Raised when the client window is resizing.
         *
         * @return {Void}
         */
        onWindowResize: debounce(() => {
            EventBus.$emit('window-resize');
        }, 150),

        detectMobile() {
            this.isMobile = window.innerWidth <= 768;
            this.isTablet = window.innerWidth <= 1024;
            console.log(`Is Mobile: ${this.isMobile}`);
        },
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
    },
});
